import React, { useEffect, useMemo, useState } from 'react'
import Button from '../../../ui/Button/Button'
import Field from '../../../ui/Field/Field'
import Icon from '../../../ui/Icon/Icon'
import RelatedProductSearch from '../UtmBuilder/RelatedProductSearch'
import GiftFundCategoryPicker from './GiftFundCategoryPicker'
import GiftFundBrandPoolTable from './GiftFundBrandPoolTable'
import {
  CATALOG_SOURCE_LABELS,
} from '../../../api/marketing/searchRelatedProducts'
import loadSelectableBrands from '../../../api/marketing/loadSelectableBrands'
import {
  GIFT_FUND_CATALOG_FILTERS,
  emptyCatalogFundFields,
  getCatalogProductImageUrl,
  getCatalogProductSiteUrl,
} from '../../../api/marketing/gifts/giftFundHelpers'
import {
  GIFT_FUND_CATALOG_SELECTION_KIND,
  GIFT_FUND_REMAINS_STATUS,
  catalogProductKey,
  formatGiftFundSkuCountLabel,
  getCatalogSelectionFromItem,
  getCategoryPoolCounts,
  getOnlineCatalogRemainQuantity,
  isCatalogProductOfferable,
  resolveBrandPoolMembers,
  resolveCategoryPoolMembers,
  toCatalogProductRef,
  toggleCatalogPoolParticipation,
  withCatalogSelectionKind,
} from '../../../api/marketing/gifts/giftFundCatalogPool'
import ActiveStatusBadge from '../../../ui/ActiveStatusBadge/ActiveStatusBadge'
import NoAvatar from '../../../ui/NoAvatar/NoAvatar'
import styles from './GiftsFund.module.scss'

const CATALOG_MODE_OPTIONS = [
  { kind: GIFT_FUND_CATALOG_SELECTION_KIND.products, label: 'Окремі товари' },
  { kind: GIFT_FUND_CATALOG_SELECTION_KIND.category, label: 'Категорія' },
  { kind: GIFT_FUND_CATALOG_SELECTION_KIND.brand, label: 'Бренд' },
]

function CatalogModeSwitcher({ kind, onChange }) {
  return (
    <div className={styles.typeSwitcherBlock}>
      <div className={styles.sourceTypeLabel}>Набір</div>
      <div className={styles.typeSwitcher} role="radiogroup" aria-label="Набір з каталогу">
        {CATALOG_MODE_OPTIONS.map((option) => {
          const isActive = kind === option.kind
          return (
            <button
              key={option.kind}
              type="button"
              role="radio"
              aria-checked={isActive}
              className={[styles.typeOption, isActive ? styles.typeOptionActive : ''].filter(Boolean).join(' ')}
              onClick={() => onChange(option.kind)}
            >
              {option.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function ProductRefRow({ title, meta, hint, onRemove, removeLabel }) {
  return (
    <div className={styles.poolRefRow}>
      <div className={styles.poolRefBody}>
        <div className={styles.poolRefTitle}>{title}</div>
        {meta ? <div className={styles.poolRefMeta}>{meta}</div> : null}
        {hint ? <div className={styles.poolRefHint}>{hint}</div> : null}
      </div>
      <button type="button" className={styles.poolRefRemove} title={removeLabel} onClick={onRemove}>
        <Icon name="times" weight="regular" size={12} />
      </button>
    </div>
  )
}

function GiftFundCatalogFields({
  draft,
  onChange,
  catalogAvailability,
  productSearchIndex,
  isProductsLoading,
  productsLoadError,
  loadProductCatalog,
  isLoadingProduct,
}) {
  const selection = getCatalogSelectionFromItem(draft)
  const {
    products = [],
    productsByKey = {},
    taxonomyNodes = [],
    remainsByShop = null,
    remainsStatus = GIFT_FUND_REMAINS_STATUS.idle,
    remainsError = '',
  } = catalogAvailability || {}

  const isBrand = selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.brand
  const isCategory = selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.category
  const isProducts = selection.kind === GIFT_FUND_CATALOG_SELECTION_KIND.products

  const members = useMemo(() => {
    if (isBrand) {
      return resolveBrandPoolMembers(products, selection.brand?.brandId)
    }
    if (isCategory) {
      return resolveCategoryPoolMembers(products, selection.category?.catalogTaxonomyNodeId)
    }
    return []
  }, [isBrand, isCategory, products, selection.brand, selection.category])

  const memberIds = useMemo(() => new Set(members.map((product) => String(product.id))), [members])
  const counts = useMemo(
    () =>
      getCategoryPoolCounts({
        members,
        excludedProductRefs: selection.excludedProductRefs,
        remainsByShop,
        remainsStatus,
      }),
    [members, remainsByShop, remainsStatus, selection.excludedProductRefs]
  )

  const exclusionKeys = useMemo(
    () => new Set(selection.excludedProductRefs.map((ref) => catalogProductKey(ref.source, ref.id))),
    [selection.excludedProductRefs]
  )

  const [exceptionQuery, setExceptionQuery] = useState('')
  const [brandOptions, setBrandOptions] = useState([])
  const [isBrandsLoading, setIsBrandsLoading] = useState(false)
  const [brandsLoadError, setBrandsLoadError] = useState('')

  useEffect(() => {
    if (!isBrand) {
      return undefined
    }

    let cancelled = false
    setIsBrandsLoading(true)
    setBrandsLoadError('')

    loadSelectableBrands()
      .then((brands) => {
        if (!cancelled) {
          setBrandOptions(Array.isArray(brands) ? brands : [])
        }
      })
      .catch((error) => {
        console.error('[GiftsFund] Failed to load brands:', error)
        if (!cancelled) {
          setBrandsLoadError('Не вдалося завантажити список брендів')
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsBrandsLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [isBrand])

  const brandSelectOptions = useMemo(() => {
    const options = brandOptions.map((brand) => ({ value: brand.id, label: brand.name }))
    const currentId = selection.brand?.brandId
    if (currentId && !options.some((option) => option.value === currentId)) {
      options.unshift({
        value: currentId,
        label: selection.brand?.brandName || currentId,
      })
    }
    return options
  }, [brandOptions, selection.brand])

  const exceptionCandidates = useMemo(() => {
    const query = exceptionQuery.trim().toLowerCase()
    return members
      .filter((product) => !exclusionKeys.has(catalogProductKey('premium', product.id)))
      .filter((product) => {
        if (!query) {
          return true
        }
        const haystack = [product.name, product.barcode, product.brandName].filter(Boolean).join(' ').toLowerCase()
        return haystack.includes(query)
      })
      .slice(0, 30)
  }, [members, exclusionKeys, exceptionQuery])

  const orphanExclusions = useMemo(
    () => selection.excludedProductRefs.filter((ref) => !memberIds.has(String(ref.id))),
    [memberIds, selection.excludedProductRefs]
  )

  const patchSelection = (nextSelection, extra = {}) => {
    onChange((prev) => ({
      ...prev,
      catalogSelection: nextSelection,
      ...extra,
    }))
  }

  const setKind = (kind) => {
    if (kind === selection.kind) {
      return
    }

    patchSelection(withCatalogSelectionKind(selection, kind))
  }

  const addProductRef = (product) => {
    if (!product) {
      return
    }

    const ref = toCatalogProductRef(product)
    if (!ref.id) {
      return
    }

    const exists = selection.productRefs.some((row) => catalogProductKey(row.source, row.id) === catalogProductKey(ref.source, ref.id))
    if (exists) {
      return
    }

    const productRefs = [...selection.productRefs, ref]
    const extra =
      productRefs.length === 1
        ? {
            catalogProductId: ref.id,
            catalogSource: ref.source,
            catalogTitle: ref.title,
            catalogBarcode: ref.barcode,
            catalogBrandName: ref.brandName,
          }
        : emptyCatalogFundFields()

    patchSelection({ ...selection, kind: GIFT_FUND_CATALOG_SELECTION_KIND.products, productRefs }, extra)
  }

  const removeProductRef = (ref) => {
    const productRefs = selection.productRefs.filter(
      (row) => catalogProductKey(row.source, row.id) !== catalogProductKey(ref.source, ref.id)
    )
    if (productRefs.length === 1) {
      const only = productRefs[0]
      patchSelection({ ...selection, productRefs }, {
        catalogProductId: only.id,
        catalogSource: only.source,
        catalogTitle: only.title,
        catalogBarcode: only.barcode,
        catalogBrandName: only.brandName,
      })
      return
    }
    patchSelection({ ...selection, productRefs }, emptyCatalogFundFields())
  }

  const addExclusion = (product) => {
    const nextExclusions = toggleCatalogPoolParticipation(selection.excludedProductRefs, product, false)
    patchSelection({
      ...selection,
      excludedProductRefs: nextExclusions,
    })
  }

  const removeExclusion = (ref) => {
    patchSelection({
      ...selection,
      excludedProductRefs: toggleCatalogPoolParticipation(selection.excludedProductRefs, ref, true),
    })
  }

  const setParticipation = (product, participating) => {
    patchSelection({
      ...selection,
      excludedProductRefs: toggleCatalogPoolParticipation(
        selection.excludedProductRefs,
        product,
        participating
      ),
    })
  }

  const setBrand = (event) => {
    const brandId = event.target.value || null
    const option = brandSelectOptions.find((row) => row.value === brandId)
    patchSelection({
      ...selection,
      kind: GIFT_FUND_CATALOG_SELECTION_KIND.brand,
      brand: brandId
        ? {
            brandId,
            brandName: option?.label || selection.brand?.brandName || '',
          }
        : null,
    })
  }

  const singleRef = isProducts && selection.productRefs.length === 1
    ? selection.productRefs[0]
    : null

  const liveRemain = singleRef
    ? getOnlineCatalogRemainQuantity(singleRef.id, singleRef.source, remainsByShop, remainsStatus)
    : { status: remainsStatus, quantity: null }

  const remainLabel =
    remainsStatus === GIFT_FUND_REMAINS_STATUS.ready && liveRemain.quantity != null
      ? `${liveRemain.quantity} шт.`
      : '—'

  const catalogImageUrl = singleRef
    ? getCatalogProductImageUrl({
        productId: singleRef.id,
        fileName: draft.catalogAvatarFileName,
        catalogSource: singleRef.source,
      })
    : null
  const catalogSiteUrl = singleRef
    ? getCatalogProductSiteUrl({ productId: singleRef.id, catalogSource: singleRef.source })
    : null

  const offerableLabel =
    remainsStatus === GIFT_FUND_REMAINS_STATUS.ready && counts.offerable != null
      ? formatGiftFundSkuCountLabel(counts.offerable)
      : '—'

  return (
    <div className={styles.catalogColumnBody}>
      <CatalogModeSwitcher kind={selection.kind} onChange={setKind} />

      {isProducts ? (
        <>
          <RelatedProductSearch
            label={selection.productRefs.length > 0 ? 'Додати товар' : 'Товар'}
            value={null}
            onChange={addProductRef}
            searchIndex={productSearchIndex || []}
            filters={GIFT_FUND_CATALOG_FILTERS}
            isLoading={isProductsLoading || isLoadingProduct}
            loadError={productsLoadError}
            onLoadRequest={loadProductCatalog}
          />

          {selection.productRefs.length > 1 ? (
            <div className={styles.poolList}>
              {selection.productRefs.map((ref) => {
                const product = productsByKey[catalogProductKey(ref.source, ref.id)]
                const remain = getOnlineCatalogRemainQuantity(ref.id, ref.source, remainsByShop, remainsStatus)
                const hints = []
                if (!product) hints.push('не знайдено')
                else if (product.isArchived) hints.push('архів')
                else if (product.isActive !== true) hints.push('неактивний')
                if (remain.status === GIFT_FUND_REMAINS_STATUS.ready && remain.quantity <= 0) hints.push('немає в наявності')
                return (
                  <ProductRefRow
                    key={catalogProductKey(ref.source, ref.id)}
                    title={ref.title || product?.name || ref.id}
                    meta={[ref.barcode || product?.barcode, CATALOG_SOURCE_LABELS[ref.source] || ref.source]
                      .filter(Boolean)
                      .join(' · ')}
                    hint={hints.join(' · ')}
                    removeLabel="Прибрати"
                    onRemove={() => removeProductRef(ref)}
                  />
                )
              })}
            </div>
          ) : null}

          {singleRef ? (
            <div className={styles.catalogProductCard}>
              <div className={styles.catalogProductMedia}>
                {catalogImageUrl ? (
                  <img src={catalogImageUrl} alt={singleRef.title || ''} />
                ) : (
                  <NoAvatar />
                )}
              </div>
              <div className={styles.catalogProductBody}>
                <div className={styles.catalogProductTitle}>{singleRef.title || singleRef.id}</div>
                {draft.catalogDescriptionShort ? (
                  <p className={styles.catalogProductDescription}>{draft.catalogDescriptionShort}</p>
                ) : null}
                <div className={styles.catalogProductRows}>
                  <div>
                    <span className={styles.catalogProductLabel}>Код товару</span>
                    <span className={styles.catalogProductValueMono}>
                      {singleRef.barcode || draft.catalogBarcode || '—'}
                    </span>
                  </div>
                  <div>
                    <span className={styles.catalogProductLabel}>Бренд</span>
                    <span>{singleRef.brandName || draft.catalogBrandName || '—'}</span>
                  </div>
                  <div>
                    <span className={styles.catalogProductLabel}>Каталог</span>
                    <span>{CATALOG_SOURCE_LABELS[singleRef.source] || singleRef.source || '—'}</span>
                  </div>
                  <div>
                    <span className={styles.catalogProductLabel}>Залишок</span>
                    <span>{remainLabel}</span>
                  </div>
                  <div>
                    <span className={styles.catalogProductLabel}>Статус</span>
                    <span>
                      {isLoadingProduct ? (
                        'завантаження…'
                      ) : draft.catalogUnavailable ? (
                        'недоступний'
                      ) : draft.catalogIsArchived ? (
                        'в архіві'
                      ) : (
                        <ActiveStatusBadge isActive={draft.catalogIsActive === true} />
                      )}
                    </span>
                  </div>
                </div>
                {catalogSiteUrl && !draft.catalogUnavailable ? (
                  <div className={styles.catalogProductActions}>
                    <Button
                      title="Переглянути на сайті"
                      theme="solid"
                      fill="accent"
                      size={28}
                      onClick={(event) => {
                        event.stopPropagation()
                        window.open(catalogSiteUrl, '_blank', 'noopener,noreferrer')
                      }}
                    />
                    <Button
                      title="Прибрати"
                      theme="bounded"
                      size={28}
                      onClick={() => removeProductRef(singleRef)}
                    />
                  </div>
                ) : (
                  <div className={styles.catalogProductActions}>
                    <Button title="Прибрати" theme="bounded" size={28} onClick={() => removeProductRef(singleRef)} />
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </>
      ) : null}

      {isCategory ? (
        <>
          <GiftFundCategoryPicker
            value={selection.category}
            nodes={taxonomyNodes}
            onChange={(category) =>
              patchSelection({
                ...selection,
                kind: GIFT_FUND_CATALOG_SELECTION_KIND.category,
                category,
              })
            }
          />

          {selection.category?.catalogTaxonomyNodeId ? (
            <>
              <p className={styles.poolCounts}>
                У категорії {counts.inCategory}
                {' · '}
                виключено {counts.excludedInCategory}
                {' · '}
                після винятків {counts.afterExclusions}
                {' · '}
                доступно {offerableLabel}
              </p>
              {remainsStatus === GIFT_FUND_REMAINS_STATUS.loading ? (
                <p className={styles.poolNote}>Завантаження наявності…</p>
              ) : null}
              {remainsStatus === GIFT_FUND_REMAINS_STATUS.error ? (
                <p className={styles.catalogWarn}>{remainsError || 'Не вдалося завантажити наявність'}</p>
              ) : null}

              <div className={styles.poolSection}>
                <p className={styles.sectionLabel}>Винятки</p>
                <Field
                  fieldType="input"
                  config={{ label: 'Знайти в категорії', type: 'text' }}
                  value={exceptionQuery}
                  changed={(event) => setExceptionQuery(event.target.value)}
                  isChangable
                />
                {exceptionQuery.trim() ? (
                  <div className={styles.poolList}>
                    {exceptionCandidates.length === 0 ? (
                      <p className={styles.empty}>Нічого не знайдено</p>
                    ) : (
                      exceptionCandidates.map((product) => (
                        <button
                          key={product.id}
                          type="button"
                          className={styles.poolCandidate}
                          onClick={() => addExclusion(product)}
                        >
                          {product.name || product.id}
                          {product.barcode ? ` · ${product.barcode}` : ''}
                        </button>
                      ))
                    )}
                  </div>
                ) : null}

                {selection.excludedProductRefs.length > 0 ? (
                  <div className={styles.poolList}>
                    {selection.excludedProductRefs.map((ref) => {
                      const inCategoryNow = memberIds.has(String(ref.id))
                      const product = productsByKey[catalogProductKey(ref.source, ref.id)]
                      const hints = []
                      if (!inCategoryNow) hints.push('не в категорії')
                      if (product?.isArchived) hints.push('архів')
                      else if (product && product.isActive !== true) hints.push('неактивний')
                      return (
                        <ProductRefRow
                          key={catalogProductKey(ref.source, ref.id)}
                          title={ref.title || product?.name || ref.id}
                          meta={ref.barcode || product?.barcode || ''}
                          hint={hints.join(' · ')}
                          removeLabel="Зняти виняток"
                          onRemove={() => removeExclusion(ref)}
                        />
                      )
                    })}
                  </div>
                ) : (
                  <p className={styles.poolNote}>Немає винятків</p>
                )}
              </div>

              <div className={styles.poolSection}>
                <p className={styles.sectionLabel}>Склад категорії</p>
                <div className={styles.poolList}>
                  {members.length === 0 ? (
                    <p className={styles.empty}>У категорії немає товарів</p>
                  ) : (
                    members.map((product) => {
                      const excluded = exclusionKeys.has(catalogProductKey('premium', product.id))
                      const remain = getOnlineCatalogRemainQuantity(
                        product.id,
                        'premium',
                        remainsByShop,
                        remainsStatus
                      )
                      const offerable = !excluded && isCatalogProductOfferable(product, remain)
                      const hints = []
                      if (excluded) hints.push('виключено')
                      else if (product.isArchived) hints.push('архів')
                      else if (product.isActive !== true) hints.push('неактивний')
                      else if (remain.status === GIFT_FUND_REMAINS_STATUS.ready && remain.quantity <= 0) {
                        hints.push('немає в наявності')
                      } else if (offerable) hints.push('доступний')
                      return (
                        <div key={product.id} className={styles.poolMemberRow}>
                          <div className={styles.poolRefBody}>
                            <div className={styles.poolRefTitle}>{product.name || product.id}</div>
                            <div className={styles.poolRefMeta}>
                              {[product.barcode, hints.join(' · ')].filter(Boolean).join(' · ')}
                            </div>
                          </div>
                          {excluded ? (
                            <Button
                              title="Зняти"
                              theme="bounded"
                              size={24}
                              onClick={() =>
                                removeExclusion({ source: 'premium', id: product.id })
                              }
                            />
                          ) : (
                            <Button
                              title="Виключити"
                              theme="bounded"
                              size={24}
                              onClick={() => addExclusion(product)}
                            />
                          )}
                        </div>
                      )
                    })
                  )}
                </div>
              </div>
            </>
          ) : null}
        </>
      ) : null}

      {isBrand ? (
        <>
          <Field
            fieldType="select"
            config={{
              label: 'Бренд',
              getOptions: brandSelectOptions,
              isSearchable: true,
            }}
            value={selection.brand?.brandId || ''}
            changed={setBrand}
            isChangable
          />
          {isBrandsLoading ? <p className={styles.poolNote}>Завантаження брендів…</p> : null}
          {brandsLoadError ? <p className={styles.catalogWarn}>{brandsLoadError}</p> : null}

          {selection.brand?.brandId ? (
            <>
              <p className={styles.poolCounts}>
                У бренді {counts.inPool}
                {' · '}
                виключено {counts.excludedInPool}
                {' · '}
                доступно до вибору{' '}
                {remainsStatus === GIFT_FUND_REMAINS_STATUS.ready && counts.offerable != null
                  ? counts.offerable
                  : '—'}
              </p>
              {remainsStatus === GIFT_FUND_REMAINS_STATUS.loading ? (
                <p className={styles.poolNote}>Завантаження наявності…</p>
              ) : null}
              {remainsStatus === GIFT_FUND_REMAINS_STATUS.error ? (
                <p className={styles.catalogWarn}>{remainsError || 'Не вдалося завантажити наявність'}</p>
              ) : null}

              <GiftFundBrandPoolTable
                members={members}
                excludedProductRefs={selection.excludedProductRefs}
                remainsByShop={remainsByShop}
                remainsStatus={remainsStatus}
                onToggleParticipation={setParticipation}
              />

              {orphanExclusions.length > 0 ? (
                <div className={styles.poolSection}>
                  <p className={styles.sectionLabel}>Винятки поза складом</p>
                  <p className={styles.poolNote}>
                    Товар уже не входить до бренда, виняток можна зняти.
                  </p>
                  <div className={styles.poolList}>
                    {orphanExclusions.map((ref) => {
                      const product = productsByKey[catalogProductKey(ref.source, ref.id)]
                      return (
                        <ProductRefRow
                          key={catalogProductKey(ref.source, ref.id)}
                          title={ref.title || product?.name || ref.id}
                          meta={ref.barcode || product?.barcode || ''}
                          hint="не в бренді"
                          removeLabel="Зняти виняток"
                          onRemove={() => removeExclusion(ref)}
                        />
                      )
                    })}
                  </div>
                </div>
              ) : null}
            </>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

export default GiftFundCatalogFields
